import React, { FC, useState } from 'react'
import { ILoanPaymentPlanModel } from '@linqpal/models'
import { IDownPaymentDetails } from '@linqpal/models/src/types/routes'
import { PaymentPlanModal } from './PaymentPlan/PaymentPlanModal'
import { DownPaymentModal } from './DownPayment/DownPaymentModal'
import { Invoice } from '../../InvoicesTab/Invoice/InvoiceDetails'
import { useInvoiceDetailsContext } from '../../TabInvoice/InvoiceDetailsContext'
import RootStore from '../../../../store/RootStore'
import { LoanAppliedSuccessPrompt } from '../../InvoicesTab/Invoice/modals'
import { ApplicationType } from '@linqpal/models/src/dictionaries/applicationType'
import { paths } from '../../../links'
import { useStore } from '../../../../store'
import { InvoiceCreditPaymentModal } from './Payment/InvoiceCreditPaymentModal'

enum Steps {
  NONE,
  PAYMENT_PLAN,
  DOWN_PAYMENT,
  PAY,
  NOTIFY_SUCCESS,
}

interface IProps {
  invoices: Invoice[]
  navigation: any
  notifySuccess?: boolean
  onClose: () => void
}

// TODO: VK: Next refactoring
// 1. replace getPlans route with getPaymentOptions to move payment calculations to the server side
//    it will return { paymentPlan, downPayment, schedule, fee }
//    remove all calculations from the client side
//    use the same server-side calculation service to get list of options to select and to calculate payment when starting DE
//    reuse this for integrations too
// 2. consider unifying this with ApplicationWithPaymentFlow
//    reuse same flow with same components,
//    normal payment would be hosted in BtModal, application payment - in PaymentStepContainer

export const InvoiceCreditPaymentFlow: FC<IProps> = ({
  invoices,
  notifySuccess = true,
  navigation,
  onClose,
}) => {
  const { userStore } = useStore()

  const [step, setStep] = useState(Steps.PAYMENT_PLAN)

  const [paymentPlan, setPaymentPlan] = useState<ILoanPaymentPlanModel | null>(
    null,
  )
  const [downPayment, setDownPayment] = useState<IDownPaymentDetails | null>(
    null,
  )

  // backward compatibility, to support project selection flow in legacy invoice details
  // TODO: move SelectPurchaseType to this flow and remove these dependencies
  const invoiceDetailsContext = useInvoiceDetailsContext()
  const { paymentPlanModalVisibility, setPaymentPlanModalVisibility } =
    invoiceDetailsContext

  const handlePaymentPlanSelection = (
    loanPaymentPlan: ILoanPaymentPlanModel,
    downPaymentDetails: IDownPaymentDetails | null,
  ) => {
    setPaymentPlan(loanPaymentPlan)
    setDownPayment(downPaymentDetails)
  }

  const handleClose = () => {
    setPaymentPlanModalVisibility(false)

    setStep(Steps.PAYMENT_PLAN)

    setPaymentPlan(null)
    setDownPayment(null)

    onClose()
  }

  const handlePaymentSuccess = () => {
    // 'What is this project for?' modal depends on paymentPlanModalVisibility - ensure it's reset
    setPaymentPlanModalVisibility(false)

    setStep(notifySuccess ? Steps.NOTIFY_SUCCESS : Steps.PAYMENT_PLAN)

    setPaymentPlan(null)
    setDownPayment(null)
  }

  const handleFlowComplete = () => {
    setStep(Steps.PAYMENT_PLAN)

    userStore.fetchDocument(ApplicationType.Credit)
    navigation.replace(paths.Console._self)
  }

  const downPaymentSettings =
    RootStore.userStore.company?.settings?.downPaymentDetails

  const useDownPayment =
    downPaymentSettings?.isRequired &&
    invoices?.some((invoice) => !!invoice.supplierInvitationDetails)

  return (
    <>
      {step === Steps.NONE && <></>}

      {paymentPlanModalVisibility && step === Steps.PAYMENT_PLAN && (
        <PaymentPlanModal
          invoices={invoices}
          paymentPlan={paymentPlan}
          onSelect={handlePaymentPlanSelection}
          onContinue={() => {
            setStep(useDownPayment ? Steps.DOWN_PAYMENT : Steps.PAY)
            // do not reset paymentPlanModalVisibility here - it will broke prequal with invoice submission flow
          }}
          onClose={handleClose}
        />
      )}

      {step === Steps.DOWN_PAYMENT && !!paymentPlan && !!downPayment && (
        <DownPaymentModal
          downPayment={downPayment}
          onContinue={(options: IDownPaymentDetails) => {
            setDownPayment(options)
            setStep(Steps.PAY)
          }}
          onBack={() => setStep(Steps.PAYMENT_PLAN)}
          onClose={handleClose}
        />
      )}

      {step === Steps.PAY && !!paymentPlan && (
        <InvoiceCreditPaymentModal
          paymentPlan={paymentPlan}
          downPayment={downPayment}
          invoiceIds={invoices.map((i) => i._id)}
          navigation={navigation}
          onBack={() => {
            setStep(useDownPayment ? Steps.DOWN_PAYMENT : Steps.PAYMENT_PLAN)
          }}
          onClose={handleClose}
          onSuccess={handlePaymentSuccess}
        />
      )}

      {step === Steps.NOTIFY_SUCCESS && (
        <LoanAppliedSuccessPrompt onClose={handleFlowComplete} />
      )}
    </>
  )
}
